import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';
import '../superadmin/DistrictsList.css';

const StudentIntakeList = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [successMessage, setSuccessMessage] = useState(null);
  const [importStatuses, setImportStatuses] = useState({}); // { studentId: { hasImportedData: boolean } }
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const timeoutRef = useRef(null);

  // Fetch students only on mount
  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Handle success message from URL params (separate effect)
  useEffect(() => {
    const message = searchParams.get('success');
    const studentName = searchParams.get('studentName');
    
    if (message === 'true' && studentName) {
      setSuccessMessage(`Submission complete for ${studentName}`);
      
      // Clear only success-related params, preserve others
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('success');
      newParams.delete('studentName');
      // Use replace: true to avoid pushing a new history entry
      setSearchParams(newParams, { replace: true });
      
      // Auto-dismiss after 5 seconds with cleanup
      timeoutRef.current = setTimeout(() => {
        setSuccessMessage(null);
        timeoutRef.current = null;
      }, 5000);
    }

    // Cleanup timeout on unmount or when effect re-runs
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [searchParams, setSearchParams]); // Only depend on searchParams for message handling

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/student-intake/my-intake');
      const studentsData = response.data;
      setStudents(studentsData);

      // Check import status for all students in one request (parent-only - district/super admins don't use this)
      if (studentsData.length > 0 && user?.role === 'parent') {
        const ids = studentsData.map(s => s.id).join(',');
        const batchResponse = await axios.get(`/api/returning-student-imports/check-batch?ids=${ids}`).catch(() => ({ data: {} }));
        setImportStatuses(batchResponse.data || {});
      } else {
        setImportStatuses({});
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportReturningStudents = async () => {
    try {
      setImporting(true);
      setImportResults(null);
      
      const response = await axios.post('/api/returning-student-imports/import');
      
      setImportResults(response.data);
      
      // Refresh import statuses
      await fetchStudents();
    } catch (error) {
      console.error('Import error:', error);
      console.error('Import error response:', error.response?.data);
      setImportResults({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to import student data'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/student-intake/my-intake/${id}`);
      setStudents(students.filter(s => s.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete student intake:', error);
      alert('Failed to delete student intake record');
    }
  };


  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '-';
    try {
      const dob = new Date(dateOfBirth);
      if (isNaN(dob.getTime())) return '-'; // Invalid date
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age >= 0 ? age : '-'; // Return '-' for negative ages
    } catch (error) {
      console.error('Error calculating age:', error);
      return '-';
    }
  };

  if (loading) {
    return <LoadingState message="Loading your students..." fullScreen />;
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>My Students</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={handleImportReturningStudents}
            disabled={importing || students.length === 0}
            className="btn-secondary"
            style={{
              opacity: (importing || students.length === 0) ? 0.6 : 1,
              cursor: (importing || students.length === 0) ? 'not-allowed' : 'pointer'
            }}
            title={students.length === 0 ? 'Add students first before importing' : 'Import available data for returning students'}
          >
            {importing ? 'Importing...' : 'Import Current Students'}
          </button>
          <Link to="/parent/student-intake/new" className="btn-primary">Add a New Student</Link>
        </div>
      </div>

      {importResults && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: importResults.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${importResults.success ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: '4px',
          color: importResults.success ? '#155724' : '#721c24'
        }}>
          {importResults.success ? (
            <div>
              <strong>Import Results:</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                {importResults.results?.map((result, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>
                    {result.studentName}: {result.status === 'imported' ? '✅ Imported successfully' : 'ℹ️ Already imported'}
                  </li>
                ))}
              </ul>
              {importResults.imported > 0 && (
                <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>
                  {importResults.imported} student(s) imported successfully.
                </p>
              )}
              {importResults.alreadyImported > 0 && (
                <p style={{ margin: '0.5rem 0 0 0' }}>
                  {importResults.alreadyImported} student(s) were already imported.
                </p>
              )}
            </div>
          ) : (
            <div>
              <strong>Import Failed:</strong> {importResults.error || 'Unknown error'}
            </div>
          )}
        </div>
      )}

      {students.length > 0 && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          background: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '4px',
          color: '#004085',
          fontSize: '0.9rem'
        }}>
          <strong>Note:</strong> The "Import Current Students" button will check for student records matching your students' last name and last 4 digits of SSN. 
          You must add your students first before the data import will match. This will import available data for returning students.
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{successMessage}</span>
          <button
            onClick={() => {
              // Clear timeout if still active
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setSuccessMessage(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#155724',
              fontSize: '1.2rem',
              cursor: 'pointer',
              padding: '0 0.5rem'
            }}
          >
            ×
          </button>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Student</h3>
            <p>Are you sure you want to delete this student's information? This action cannot be undone.</p>
            <div className="modal-actions">
              <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}


      {students.length === 0 ? (
        <EmptyState
          icon="👤"
          title="No Students Yet"
          message="Get started by adding your first student. You'll be able to complete enrollment forms for each student you add."
          action={<Link to="/parent/student-intake/new" className="btn-primary">Add Your First Student</Link>}
        />
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name (Last, First)</th>
                <th>Age</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => (
              <tr key={student.id}>
                  <td>{student?.last_name || ''}, {student?.first_name || ''}</td>
                  <td>{calculateAge(student?.date_of_birth)}</td>
                  <td className="actions">
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {student.form_status === 'completed' ? (
                        <Link
                          to={`/parent/edit-student/${student.id}`}
                          className="btn-edit"
                          style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}
                          title="Edit submitted enrollment form"
                        >
                          Edit Submission
                        </Link>
                      ) : student.form_status === 'partial' ? (
                        <Link
                          to={`/parent/new-student/${student.id}`}
                          className="btn-edit"
                          style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem', backgroundColor: '#ffc107', borderColor: '#ffc107' }}
                          title="Continue filling out enrollment forms"
                        >
                          Continue Enrollment
                        </Link>
                      ) : (
                        <Link
                          to={`/parent/new-student/${student.id}`}
                          className="btn-edit"
                          style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem' }}
                          title="Fill out enrollment forms for this student"
                        >
                          Start Enrollment
                        </Link>
                      )}
                      {importStatuses[student?.id]?.hasImportedData ? (
                        <Link
                          to={`/parent/returning-student-enrollment/${student.id}`}
                          className="btn-secondary"
                          style={{
                            fontSize: '0.85em',
                            padding: '0.4rem 0.8rem',
                            backgroundColor: '#6c757d',
                            borderColor: '#6c757d',
                            textDecoration: 'none',
                            display: 'inline-block',
                            textAlign: 'center'
                          }}
                          title="Fill out returning student enrollment form"
                        >
                          Returning Student
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="btn-secondary"
                          style={{
                            fontSize: '0.85em',
                            padding: '0.4rem 0.8rem',
                            backgroundColor: '#6c757d',
                            borderColor: '#6c757d',
                            opacity: 0.5,
                            cursor: 'not-allowed'
                          }}
                          title="Student record not found in database. Have you run the import?"
                        >
                          Returning Student
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm(student?.id)}
                        className="btn-delete"
                        style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem' }}
                        title="Delete this student record"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StudentIntakeList;

